import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

import { useLoginMutation } from "@/features/auth/api/authApi";
import { isAccountInactive } from "@/lib/authFailure";
import { setTempLogin } from "@/store/slices/authSlice";
import { safeStorage } from "@/shared/lib/safeStorage";
import { cn } from "@/shared/lib/cn";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form/Form";
import { Input } from "@/shared/ui/input/Input";
import { Button } from "@/shared/ui/button/Button";

import { AUTH_STORAGE_KEYS } from "../lib/storageKeys";
import { AuthCard, AuthShell } from "./AuthShell";

const loginSchema = z.object({
  merchantId: z.string().trim().min(1, "Merchant ID is required."),
  username: z.string().trim().min(1, "Username is required."),
  password: z.string().min(1, "Password is required."),
});

type LoginFormValues = z.infer<typeof loginSchema>;

/**
 * PayGate sign-in — rendered in the shared branded <AuthShell> + <AuthCard>.
 * Functionality preserved: merchant/username/password → OTP step (per dev).
 */
export function LoginView() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [login, { isLoading }] = useLoginMutation();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { merchantId: "", username: "", password: "" },
  });

  useEffect(() => {
    safeStorage.removeItem(AUTH_STORAGE_KEYS.USER_DETAILS);
  }, []);

  const onSubmit = useCallback(
    async (values: LoginFormValues) => {
      try {
        const res = await login(values).unwrap();
        const details = {
          merchantId: values.merchantId,
          username: values.username,
          emailId: res.emailId,
          phoneNumber: res.phoneNumber,
        };
        safeStorage.setItem(AUTH_STORAGE_KEYS.USER_DETAILS, JSON.stringify(details));
        dispatch(setTempLogin(details));
        navigate("/verify-otp");
      } catch (err) {
        const status = (err as { status?: number } | undefined)?.status;
        if (isAccountInactive(err)) {
          // Not a credentials problem — the account was switched off, and
          // retrying with a better password will not help.
          toast.error("This account is not active. Please contact your administrator.");
          return;
        }
        toast.error(
          status === 401
            ? "Invalid credentials. Please try again."
            : "Login failed, please try again."
        );
      }
    },
    [login, dispatch, navigate]
  );

  return (
    <AuthShell>
      <AuthCard title="Sign in" subtitle="Use your merchant credentials to access the workspace.">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <FormField
              control={form.control}
              name="merchantId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Merchant ID</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter Merchant ID"
                      autoComplete="organization"
                      autoCapitalize="off"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Username</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Your username"
                      autoComplete="username"
                      autoCapitalize="off"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Password</FormLabel>
                  <FormControl>
                    <PasswordField
                      show={showPassword}
                      onToggleShow={() => setShowPassword((p) => !p)}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" size="lg" className="mt-2 w-full" isLoading={isLoading}>
              {isLoading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </Form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Need help signing in? Contact your site administrator.
        </p>
      </AuthCard>
    </AuthShell>
  );
}

// ─── Password field (show/hide toggle) ──────────────────────────────────────────
interface PasswordFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  show: boolean;
  onToggleShow: () => void;
}

const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ show, onToggleShow, className, ...props }, ref) => (
    <div className="relative">
      <Input
        ref={ref}
        type={show ? "text" : "password"}
        autoComplete="current-password"
        className={cn("pr-11", className)}
        {...props}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={onToggleShow}
        aria-label={show ? "Hide password" : "Show password"}
        aria-pressed={show}
        className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </Button>
    </div>
  )
);
PasswordField.displayName = "PasswordField";
